import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useDomain } from '@/composables/useDomain'

const { isAdmin } = useDomain()

// ─── Public / blog routes (non-admin domains) ──────────────────────────────
const publicRoutes: RouteRecordRaw[] = [
  // All public pages share PublicLayout (persistent header + footer)
  {
    path: '/',
    component: () => import('@/pages/PublicLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/pages/HomePage.vue'),
      },
      {
        path: 'news',
        name: 'blog',
        component: () => import('@/pages/HomePage.vue'),
      },
      {
        path: 'news/saved',
        name: 'saved',
        component: () => import('@/pages/SavedPage.vue'),
      },
      {
        path: 'news/page/:page(\\d+)',
        name: 'blog-page',
        component: () => import('@/pages/IndexPage.vue'),
      },
      {
        path: 'news/tag/:slug',
        name: 'tag',
        component: () => import('@/pages/TagPage.vue'),
      },
      {
        path: 'news/author/:slug',
        name: 'author',
        component: () => import('@/pages/AuthorPage.vue'),
      },
      {
        path: 'news/search',
        name: 'search',
        component: () => import('@/pages/NewsSearchPage.vue'),
      },
      {
        path: 'news/:slug',
        name: 'article',
        component: () => import('@/pages/PostPage.vue'),
      },
      {
        path: 'about',
        name: 'about',
        component: () => import('@/pages/AboutPage.vue'),
      },
      {
        path: 'subscribe',
        name: 'subscribe',
        component: () => import('@/pages/SubscribePage.vue'),
      },
    ],
  },
]

// ─── Admin routes (admin.* subdomain only) ──────────────────────────────────
const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/pages/admin/AdminLayout.vue'),
    children: [
      { path: '', redirect: '/analytics' },
      {
        path: 'analytics',
        name: 'admin-analytics',
        component: () => import('@/pages/admin/AdminAnalyticsPage.vue'),
      },
      {
        path: 'domains',
        redirect: '/countries',
      },
      {
        path: 'feeds',
        name: 'admin-feeds',
        component: () => import('@/pages/admin/AdminFeedsPage.vue'),
      },
      {
        path: 'articles',
        name: 'admin-articles',
        component: () => import('@/pages/admin/AdminArticlesPage.vue'),
      },
      {
        path: 'countries',
        name: 'admin-countries',
        component: () => import('@/pages/admin/AdminCountriesPage.vue'),
      },
      {
        path: 'jobs',
        name: 'admin-jobs',
        component: () => import('@/pages/admin/AdminJobsPage.vue'),
      },
      {
        path: 'ads',
        name: 'admin-ads',
        component: () => import('@/pages/admin/AdminAdsPage.vue'),
      },
      {
        path: 'settings',
        name: 'admin-settings',
        redirect: { name: 'admin-settings-ai' },
      },
      {
        path: 'settings/ai',
        name: 'admin-settings-ai',
        component: () => import('@/pages/admin/AdminSettingsPage.vue'),
      },
    ],
  },
  // Legacy /admin redirect for backwards compatibility
  {
    path: '/admin/:pathMatch(.*)*',
    redirect: (to) => to.path.replace(/^\/admin/, '') || '/',
  },
]

// ─── Catch-all ──────────────────────────────────────────────────────────────
const catchAll: RouteRecordRaw = {
  path: '/:pathMatch(.*)*',
  name: 'not-found',
  component: () => import('@/pages/NotFoundPage.vue'),
}

const routes: RouteRecordRaw[] = [
  ...(isAdmin ? adminRoutes : publicRoutes),
  catchAll,
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
})

export default router
